// JivoSettingsPage — админ-настройки JivoChat (/admin/jivo; Tech-Story-jivo-admin-settings,
// R-FE-2). Канон EmailSettingsPage: useQuery/useMutation + invalidateQueries; секрет
// write-only по канону SmtpAccountDialog (GET — только hasSecret + маска; PUT-ответ
// содержит newWebhookSecret один раз). URL вебхука = ${publicOrigin}/hook/<секрет> —
// конкатенация без префикса https://: VITE_PUBLIC_ORIGIN уже содержит схему (publicEnv.ts).
// При пустом publicOrigin — относительный /hook/<маска>, копирование отключено (B-1).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { jivoSettingsApi, JIVO_SECRET_MASK, type JivoSettingsAdmin } from '@/api/jivoSettings';
import { publicOrigin } from '@/config/publicEnv';
import { extractApiErrorMessage } from '@/lib/apiError';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { t } from '@/i18n';
import { JivoSettingsDialog } from './components/JivoSettingsDialog';

/** Видимый URL вебхука: полный (${publicOrigin}/hook/<маска>) или относительный при пустом origin. */
function webhookUrlDisplay(mask: string): string {
  return publicOrigin !== '' ? `${publicOrigin}/hook/${mask}` : `/hook/${mask}`;
}

/** Карточка текущих настроек: switch, поля, блок URL вебхука, кнопка «Изменить». */
function SettingsCard({ settings, onEdit }: { settings: JivoSettingsAdmin; onEdit: () => void }): JSX.Element {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <FormControlLabel
            control={<Switch checked={settings.enabled} disabled />}
            label={t('admin.jivo.enabled')}
          />
          <Typography variant="caption" color="text.secondary" component="div">
            {t('admin.jivo.enabledDisableWarning')}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2">{t('admin.jivo.widgetId')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {settings.widgetId ?? '—'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2">{t('admin.jivo.crmBaseUrl')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {settings.crmBaseUrl ?? '—'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2">{t('admin.jivo.webhookUrlBlock')}</Typography>
          {settings.hasSecret ? (
            <Typography variant="body2" data-testid="jivo-webhook-url-masked" sx={{ wordBreak: 'break-all' }}>
              {webhookUrlDisplay(settings.secretMask ?? JIVO_SECRET_MASK)}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )}
          <Typography variant="caption" color="text.secondary" component="div">
            {t('admin.jivo.webhookUrlHint')}
          </Typography>
        </Box>
        <Box>
          <Button variant="outlined" onClick={onEdit}>
            {t('admin.jivo.edit')}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}

/** Статус копирования URL: без действия / скопировано / clipboard недоступен (ручное копирование). */
type CopyStatus = 'idle' | 'copied' | 'manual';

/** Одноразовый блок после смены секрета: полный URL + copy + warning (исчезает при закрытии). */
function OneTimeSecretBlock({ newSecret, currentMask }: { newSecret: string; currentMask: string }): JSX.Element {
  const fullUrl = publicOrigin !== '' ? `${publicOrigin}/hook/${newSecret}` : null;
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');

  const handleCopy = async (): Promise<void> => {
    // fullUrl non-null гарантирован disabled-состоянием кнопки при пустом publicOrigin.
    if (fullUrl === null) {
      return;
    }
    try {
      if (navigator.clipboard === undefined) {
        // Non-secure context (http-деплой ≠ localhost): Clipboard API отсутствует.
        throw new Error('clipboard API unavailable');
      }
      await navigator.clipboard.writeText(fullUrl);
      setCopyStatus('copied');
    } catch {
      // Одноразовый секрет нельзя терять молча: URL уже показан в поле блока —
      // подсказываем ручное копирование (pintest S4 M2).
      setCopyStatus('manual');
    }
  };

  const closeCopyStatus = (): void => setCopyStatus('idle');

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          {/* Полный URL при заданном origin; иначе относительный /hook/<маска> (B-1). */}
          <Typography variant="body2" data-testid="jivo-webhook-url" sx={{ wordBreak: 'break-all' }}>
            {fullUrl ?? webhookUrlDisplay(currentMask)}
          </Typography>
          {fullUrl === null ? <Alert severity="warning">{t('admin.jivo.publicOriginMissing')}</Alert> : null}
          <Alert severity="warning">{t('admin.jivo.secretChangedWarning')}</Alert>
          <Box>
            <Button
              data-testid="jivo-copy-url"
              startIcon={<ContentCopyIcon />}
              disabled={fullUrl === null}
              onClick={handleCopy}
            >
              {t('admin.jivo.copyButton')}
            </Button>
          </Box>
        </Stack>
      </Paper>
      <Snackbar
        open={copyStatus !== 'idle'}
        autoHideDuration={4000}
        onClose={closeCopyStatus}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="jivo-copy-snackbar"
      >
        <Alert severity={copyStatus === 'copied' ? 'success' : 'warning'} onClose={closeCopyStatus}>
          {copyStatus === 'copied'
            ? t('admin.jivo.copiedFeedback')
            : t('admin.jivo.copyManualFallback')}
        </Alert>
      </Snackbar>
    </>
  );
}

/** Страница «JivoChat»: просмотр и правка настроек интеграции (admin-only роут). */
export function JivoSettingsPage(): JSX.Element {
  const [dialogOpen, setDialogOpen] = useState(false);
  /** Одноразовый секрет из PUT-ответа (state — исчезает при перезагрузке/закрытии страницы). */
  const [oneTimeSecret, setOneTimeSecret] = useState<string | null>(null);

  const settingsQuery = useQuery({
    queryKey: ['jivo', 'settings'],
    queryFn: () => jivoSettingsApi.get(),
    retry: false,
  });

  // PUT выполняет диалог; страница только фиксирует одноразовый секрет из ответа.
  const handleSaved = (updated: JivoSettingsAdmin): void => {
    setOneTimeSecret(updated.newWebhookSecret ?? null);
  };

  const renderBody = (): JSX.Element => {
    if (settingsQuery.isLoading) {
      return <LoadingSpinner label={t('common.loading')} />;
    }
    if (settingsQuery.isError || settingsQuery.data === undefined) {
      return (
        <Alert severity="error">
          {extractApiErrorMessage(settingsQuery.error, t('admin.jivo.loadError'))}
        </Alert>
      );
    }
    return (
      <Stack spacing={2}>
        <SettingsCard settings={settingsQuery.data} onEdit={() => setDialogOpen(true)} />
        {oneTimeSecret !== null ? (
          <OneTimeSecretBlock
            newSecret={oneTimeSecret}
            currentMask={settingsQuery.data.secretMask ?? JIVO_SECRET_MASK}
          />
        ) : null}
      </Stack>
    );
  };

  return (
    <Box>
      <PageHeader title={t('admin.jivo.title')} subtitle={t('admin.jivo.subtitle')} />
      <Box sx={{ mt: 2 }}>{renderBody()}</Box>
      <JivoSettingsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        settings={settingsQuery.data ?? null}
        onSaved={handleSaved}
      />
    </Box>
  );
}
